// @flow

import { v4 } from 'uuid';
import React, { Fragment, useContext } from 'react';
import type { Node } from 'react';
import { useQuery } from 'shared_services/apollo/useQuery';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { DataProviderPreloaderPage } from 'shared_components/data/providers/preloaders/Page';
import { SSRContext } from 'shared_data/providers/ssr/Context';
import { CmsMeta } from 'shared_components/cms/Meta';
import { HeadContentService } from 'shared_services/riseart/HeadContent';
import { useStoreCode } from 'shared_services/redux/hooks/useStoreCode';
import { CMS_LAYOUT_COMPONENTS } from 'shared_components/cms/layouts/index';
import RENDER_CMS_PAGE_QUERY from 'shared_data/queries/cms/renderPage.graphql';
import { raCmsContentCls } from '@riseart/antd-provider/dist/website/variables.less';

// Include CSS specific to CMS pages only
import '@riseart/antd-provider/dist/website/cms.css';

type Props = {
  id: number,
};

const PRELOADER_ID = v4();

/**
 * CmsPage
 *
 * @param {Props} props
 * @returns {Node}
 */
const CmsPage = ({ id }: Props): Node => {
  const { isSSR } = useContext(SSRContext);
  const store = useStoreCode();
  const { loading, data } = useQuery(RENDER_CMS_PAGE_QUERY, {
    skip: !store,
    variables: { id, store },
  });
  const {
    renderCmsPage: {
      layout,
      metaTitle,
      metaDescription,
      metaKeywords,
      pageTitle,
      locales,
      placeholders,
    } = {},
  } = data || {};
  const CmsLayout = !loading && layout ? CMS_LAYOUT_COMPONENTS[layout] : null;

  // Get initial head content
  const initialHeadContent =
    (typeof window !== 'undefined' &&
      window.RiseArt &&
      window.RiseArt.initialHeadContent &&
      JSON.parse(decodeURIComponent(window.RiseArt.initialHeadContent))) ||
    null;

  if (initialHeadContent) {
    delete window.RiseArt.initialHeadContent;
  }

  HeadContentService.config(initialHeadContent || [], isSSR);

  return (
    <Fragment>
      <DataProviderPreloaderPage uniqueId={PRELOADER_ID} attach={loading} />
      {loading ? <IsomorphicRipple isActive isFullScreen /> : null}
      {CmsLayout ? (
        <Fragment>
          <CmsMeta
            metaTitle={metaTitle}
            metaDescription={metaDescription}
            metaKeywords={metaKeywords}
            pageTitle={pageTitle}
            locales={locales}
          />
          <CmsLayout placeholders={placeholders} className={raCmsContentCls} />
        </Fragment>
      ) : null}
    </Fragment>
  );
};

export default CmsPage;
